import React, { useEffect, useState, useMemo } from 'react';
import { holidaysAPI } from '../../api/bookings';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { CalendarDays, Plus, Edit, Trash2, Copy, Download, Globe, Info } from 'lucide-react';
import { bruneiToday } from '../../lib/brunei';
import SiteBannerSection from './SettingsHolidays/SiteBannerSection';

const HOLIDAY_TYPES = [
  { value: 'public', label: 'Public' },
  { value: 'religious', label: 'Religious' },
  { value: 'national', label: 'National' },
  { value: 'custom', label: 'Custom' },
];

const TYPE_BADGE_CLASS = {
  public: 'bg-blue-100 text-blue-700 border border-blue-200',
  religious: 'bg-purple-100 text-purple-700 border border-purple-200',
  national: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
  custom: 'bg-amber-100 text-amber-700 border border-amber-200',
};

const emptyForm = () => ({
  date: '',
  name: '',
  type: 'public',
  active: true,
  notes: '',
});

export default function SettingsHolidays() {
  const currentYear = parseInt(bruneiToday().slice(0, 4), 10);
  const [year, setYear] = useState(currentYear);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  // Year switcher: previous, current, next, +2
  const yearOptions = useMemo(
    () => [currentYear - 1, currentYear, currentYear + 1, currentYear + 2],
    [currentYear]
  );

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  const load = async () => {
    setLoading(true);
    try {
      const data = await holidaysAPI.list({ year });
      setRows(data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load holidays');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm(), date: `${year}-01-01` });
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      date: row.date,
      name: row.name,
      type: row.type || 'public',
      active: row.active !== false,
      notes: row.notes || '',
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.date || !form.name.trim()) {
      toast.error('Date and Name are required');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await holidaysAPI.update(editing.id, form);
        toast.success('Holiday updated');
      } else {
        await holidaysAPI.create(form);
        toast.success('Holiday created');
      }
      setDialogOpen(false);
      // If date moved to a different year, follow it
      const targetYear = parseInt(form.date.slice(0, 4), 10);
      if (targetYear !== year) {
        setYear(targetYear);
      } else {
        await load();
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || 'Failed to save holiday');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (row) => {
    try {
      await holidaysAPI.update(row.id, { active: !row.active });
      toast.success(row.active ? 'Deactivated' : 'Activated');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update');
    }
  };

  const handleSoftDelete = async (row) => {
    if (!window.confirm(`Deactivate "${row.name}" on ${row.date}? It can be reactivated later.`)) return;
    try {
      await holidaysAPI.remove(row.id);
      toast.success('Holiday deactivated');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleHardDelete = async (row) => {
    const confirmInput = window.prompt(
      `PERMANENT DELETE\n"${row.name}" on ${row.date}\nThis cannot be undone.\nType DELETE to confirm:`
    );
    if (confirmInput !== 'DELETE') return;
    try {
      await holidaysAPI.remove(row.id, { hard: true });
      toast.success('Holiday permanently deleted');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleImportPreset = async () => {
    if (!window.confirm(`Import the built-in preset for ${year}?\nExisting holidays for the same date+name will be skipped.`)) return;
    try {
      const result = await holidaysAPI.importPreset(year);
      toast.success(
        `Preset imported — ${result.inserted} added, ${result.skipped} skipped`,
        { duration: 6000 }
      );
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to import preset');
    }
  };

  const handleDuplicateNextYear = async () => {
    const target = year + 1;
    if (!window.confirm(`Copy all ${year} holidays to ${target}?\nDate+name pairs already in ${target} will be skipped.`)) return;
    try {
      const result = await holidaysAPI.duplicateYear(year, target);
      toast.success(
        `Copied ${year} → ${target}: ${result.inserted} added, ${result.skipped} skipped`,
        { duration: 6000 }
      );
      setYear(target);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to duplicate year');
    }
  };

  return (
    <div data-testid="holidays-page">
      <div className="flex justify-between items-center flex-wrap gap-4 mb-6">
        <div>
          <h1 className="text-4xl font-black font-heading uppercase">Holidays</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Brunei public holidays drive <span className="font-semibold">holiday-priced</span> rules.
          </p>
        </div>
        <Button
          className="btn-volt"
          onClick={openCreate}
          data-testid="add-holiday-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Holiday
        </Button>
      </div>

      {/* Read-only timezone banner (per requirement) */}
      <Card className="p-4 mb-6 flex items-start gap-3 bg-muted/30 border-muted" data-testid="timezone-banner">
        <Globe className="w-5 h-5 text-muted-foreground mt-0.5 flex-shrink-0" />
        <div className="text-sm">
          <p className="font-semibold">Operating Timezone: Asia/Brunei (UTC+8)</p>
          <p className="text-muted-foreground mt-0.5">
            All dates are resolved server-side in Brunei local time. Holidays affect <em>new</em> bookings only —
            existing bookings keep their stored price.
          </p>
        </div>
      </Card>

      {/* Admin-controlled site-wide announcement banner */}
      <div className="mb-6" data-testid="site-banner-admin-section">
        <SiteBannerSection />
      </div>

      {/* Toolbar */}
      <Card className="p-4 mb-6">
        <div className="flex items-end gap-4 flex-wrap">
          <div>
            <Label className="uppercase text-xs font-bold text-muted-foreground mb-1.5 block">Year</Label>
            <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
              <SelectTrigger className="w-32" data-testid="year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {yearOptions.map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="outline"
            onClick={handleImportPreset}
            data-testid="import-preset-btn"
            className="ml-auto"
          >
            <Download className="w-4 h-4 mr-2" />
            Import {year} Preset
          </Button>
          <Button
            variant="outline"
            onClick={handleDuplicateNextYear}
            disabled={rows.length === 0}
            data-testid="duplicate-year-btn"
          >
            <Copy className="w-4 h-4 mr-2" />
            Duplicate to {year + 1}
          </Button>
        </div>
      </Card>

      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Loading holidays...</div>
      ) : rows.length === 0 ? (
        <Card className="p-10 text-center">
          <CalendarDays className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
          <p className="text-muted-foreground mb-2">No holidays configured for {year}.</p>
          <p className="text-xs text-muted-foreground mb-4 max-w-md mx-auto">
            Use <strong>Import {year} Preset</strong> for the standard fixed-date list,
            then add lunar/observed dates manually.
          </p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm" data-testid="holidays-table">
            <thead className="bg-muted/50">
              <tr className="text-left">
                <th className="px-4 py-3 font-bold uppercase text-xs text-muted-foreground">Date</th>
                <th className="px-4 py-3 font-bold uppercase text-xs text-muted-foreground">Name</th>
                <th className="px-4 py-3 font-bold uppercase text-xs text-muted-foreground">Type</th>
                <th className="px-4 py-3 font-bold uppercase text-xs text-muted-foreground">Active</th>
                <th className="px-4 py-3 font-bold uppercase text-xs text-muted-foreground text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.id}
                  className={`border-t ${!row.active ? 'opacity-50 bg-muted/20' : 'hover:bg-muted/20'}`}
                  data-testid={`holiday-row-${row.id}`}
                >
                  <td className="px-4 py-3 font-mono">{row.date}</td>
                  <td className="px-4 py-3 font-medium">
                    {row.name}
                    {row.notes && (
                      <span className="block text-xs text-muted-foreground">{row.notes}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold capitalize ${TYPE_BADGE_CLASS[row.type] || ''}`}>
                      {row.type}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <Switch
                      checked={!!row.active}
                      onCheckedChange={() => handleToggleActive(row)}
                      data-testid={`toggle-active-${row.id}`}
                    />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => openEdit(row)}
                        data-testid={`edit-holiday-${row.id}`}
                        title="Edit"
                      >
                        <Edit className="w-4 h-4 text-primary" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleSoftDelete(row)}
                        data-testid={`soft-delete-${row.id}`}
                        title="Deactivate"
                      >
                        <Trash2 className="w-4 h-4 text-amber-600" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleHardDelete(row)}
                        data-testid={`hard-delete-${row.id}`}
                        title="Permanently delete"
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Tie-in note */}
      <Card className="p-4 mt-6 flex items-start gap-3 bg-blue-50/40 border-blue-200">
        <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
        <p className="text-sm text-blue-900">
          Holidays only matter when a pricing rule has <code className="px-1.5 py-0.5 bg-blue-100 rounded">day_type = holiday</code>.
          {' '}Configure those in <a href="/admin/pricing" className="underline font-semibold" data-testid="pricing-link">Pricing Management</a>.
        </p>
      </Card>

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="holiday-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase">
              {editing ? 'Edit Holiday' : 'Add Holiday'}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Configure a Brunei public, religious, national or custom holiday — used to trigger holiday-priced rules.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div>
              <Label className="uppercase text-xs font-bold text-muted-foreground mb-1.5 block">
                Date *
              </Label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="h-11 rounded-xl"
                data-testid="holiday-date-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Brunei calendar date — YYYY-MM-DD.
              </p>
            </div>

            <div>
              <Label className="uppercase text-xs font-bold text-muted-foreground mb-1.5 block">
                Name *
              </Label>
              <Input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Hari Raya Aidilfitri"
                className="h-11 rounded-xl"
                data-testid="holiday-name-input"
              />
            </div>

            <div>
              <Label className="uppercase text-xs font-bold text-muted-foreground mb-1.5 block">
                Type
              </Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className="h-11 rounded-xl" data-testid="holiday-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {HOLIDAY_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="uppercase text-xs font-bold text-muted-foreground mb-1.5 block">
                Notes (optional)
              </Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="e.g. Observed Monday in lieu of Saturday."
                rows={2}
                className="rounded-xl"
                data-testid="holiday-notes-input"
              />
            </div>

            <div className="flex items-center justify-between">
              <Label className="uppercase text-xs font-bold text-muted-foreground">
                Active
              </Label>
              <Switch
                checked={form.active}
                onCheckedChange={(v) => setForm({ ...form, active: v })}
                data-testid="holiday-active-switch"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="flex-1"
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                className="flex-1 btn-volt"
                disabled={saving}
                data-testid="save-holiday-btn"
              >
                {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
